import { LogOut, ShieldCheck } from 'lucide-react';
import { OpsApiError, opsApiRequest } from '../api/client.ts';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import { Button, PageHeader } from '../ui/index.ts';

/**
 * `/landing` — the only page a role-less operator can reach. Every
 * admin route 403s for them, so this page must be self-sufficient:
 * explain the wait state, tell them who to contact, and offer the one
 * safe in-app action (logout). Deliberately no links into the app —
 * they would all bounce back here (issue #854).
 */
export function OpsLandingPage(): JSX.Element {
  const { user, clearUser, fetchImpl } = useSession();
  const t = useT();
  const displayIdentity =
    user?.displayName !== undefined && user.displayName.length > 0
      ? user.displayName
      : user?.email !== undefined && user.email.length > 0
        ? user.email
        : t('shell.signed_in_fallback');

  async function handleLogout(): Promise<void> {
    try {
      await opsApiRequest<{ ok: boolean }>('/v1/auth/logout', { method: 'POST' }, fetchImpl);
    } catch (err) {
      // Mirror the Shell's logout behavior: even if /logout fails we
      // clear the client-side session mirror; the cookie expires on
      // its own server-side TTL.
      if (!(err instanceof OpsApiError)) {
        throw err;
      }
    }
    clearUser();
  }

  return (
    <section
      className="mx-auto flex max-w-3xl flex-col gap-6 py-8"
      data-testid="ops-landing-page"
    >
      <div className="flex items-center gap-2 text-body font-medium text-ok">
        <ShieldCheck aria-hidden="true" className="h-4 w-4" />
        <span>{t('ops_landing.status')}</span>
      </div>
      <div className="flex flex-col gap-3">
        <PageHeader title={t('ops_landing.title')} />
        <p className="m-0 max-w-2xl text-body text-paper-mid">{t('ops_landing.body')}</p>
        <p className="m-0 max-w-2xl text-body text-paper-mid" data-testid="ops-landing-guidance">
          {t('ops_landing.guidance')}
        </p>
      </div>
      <dl className="m-0 grid max-w-xl grid-cols-[7rem_minmax(0,1fr)] gap-x-4 gap-y-2 text-body">
        <dt className="text-paper-low">{t('ops_landing.account')}</dt>
        <dd className="m-0 min-w-0 truncate font-medium text-paper">{displayIdentity}</dd>
        <dt className="text-paper-low">{t('ops_landing.role')}</dt>
        <dd className="m-0 font-medium text-paper">{t('ops_landing.role_pending')}</dd>
      </dl>
      <div>
        <Button
          type="button"
          onClick={() => {
            void handleLogout();
          }}
          data-testid="ops-landing-logout"
        >
          <LogOut aria-hidden="true" className="h-4 w-4" />
          {t('ops_landing.logout')}
        </Button>
      </div>
    </section>
  );
}
